import { SVG } from '@svgdotjs/svg.js';
import { NgFlowDesignerNodeDirective } from '../../ng-flow-designer-node.directive';
import { NgFlowDesignerOutputDirective } from '../../ng-flow-designer-output.directive';
import { getRandomColor } from '../../utils/randomColor';

let ghostEdgeColor = '';

export function SvgRenderer(options) {
  this.data = {};
  this.elements = {};
  this.lastZoom = this.cy.zoom();
  this.debug = options.debug;
  this.smallGridCellSize = options.motionBlur.smallGridCellSize || 10;
  this.gridCellSize = options.motionBlur.gridCellSize || 100;
  this.debugElements = {};
  this.lastLoop = Date.now();
  this.fps = 0;
  this.rerender = true;
  if (this.debug) {
    setInterval(() => {
      this.renderFps();
      this.fps = 0;
    }, 100);
  }
}

SvgRenderer.prototype.initEdgeController = function (edge: any) {
  const controllerDirective = edge.controller;

  if (!controllerDirective) {
    return;
  }

  const element = controllerDirective.host.nativeElement;
  if (!edge.showArrow) {
    element.style.display = 'none';
    return;
  }

  element.id = edge.source;
  element.style.position = 'absolute';
};

SvgRenderer.prototype.matchCanvasSize = function (container: HTMLElement) {
  if (!this.svg) {
    const { width, height } = container.getBoundingClientRect();

    // @ts-ignore
    this.svg = SVG().addTo(container).attr('id', 'svg-layer').size(width, height);

    this.drawGrid();
  }
  if (!this.html) {
    this.html = document.createElement('div');
    this.html.setAttribute('id', 'html-layer');
    container.prepend(this.html);
  }
};

SvgRenderer.prototype.drawGrid = function () {
  const pan = this.cy.pan();
  const h = parseInt(this.svg.node.getAttribute('height')) - (pan.y < 0 ? pan.y : 0);
  const w = parseInt(this.svg.node.getAttribute('width')) - (pan.x < 0 ? pan.x : 0);
  const zoom = this.cy.zoom();

  if (!this.grid) {
    this.grid = this.svg.nested().attr({
      class: 'grid-container',
    });
    const defs = this.grid.defs();

    const small = this.smallGridCellSize;
    defs
      .pattern(small, small, add => {
        add.path(`M ${small} 0 L 0 0 0 ${small}`).attr({ class: 'grid-small-line', fill: 'none' });
      })
      .attr('id', 'small-grid');

    const big = this.gridCellSize;
    defs
      .pattern(big, big, add => {
        add.rect(big, big).attr('fill', 'url(#small-grid)');
        add.path(`M ${big} 0 L 0 0 0 ${big}`).attr({ class: 'grid-line', fill: 'none' });
      })
      .attr('id', 'grid');
  }
  this.grid.clear();

  this.grid
    .rect(w / zoom, h / zoom)
    .attr('fill', 'url(#grid)')
    .attr('transform', `translate(${pan.x},${pan.y}) scale(${zoom})`)
    .move((pan.x * -1) / zoom, (pan.y * -1) / zoom);
};

SvgRenderer.prototype.redrawHint = function (group, bool) {};

SvgRenderer.prototype.tick = function () {
  const thisLoop = Date.now();
  this.fps = (1000 / (thisLoop - this.lastLoop)).toFixed(0);
  this.lastLoop = thisLoop;
};

SvgRenderer.prototype.renderFps = function () {
  if (!this.fpsElement) {
    const container = this.svg.nested().attr({ width: 100, height: 30 });

    container.rect(10, 20).attr({ x: 0, y: 0, width: 100, height: 30, stroke: 'white' });

    this.fpsElement = container.text(`fps: ${this.fps}`).attr({ x: 10, y: 20, stroke: 'white' });
  }

  this.fpsElement.clear();
  this.fpsElement.plain(`fps: ${this.fps}`);
};

SvgRenderer.prototype.render = function (group, bool) {
  if (!this.svg) {
    return;
  }

  if (this.debug) {
    this.tick();
  }
  this.drawGrid();

  Object.keys(this.elements).forEach(id => {
    const el = this.cy.getElementById(id);
    if (el._private.removed === undefined) {
      if (this.elements[id].arrow) {
        this.elements[id].arrow.remove();
      }
      this.elements[id].remove();
      delete this.elements[id];
    }
  });

  if (this.debug) {
    Object.keys(this.debugElements).forEach(id => {
      if (this.cy.getElementById(id).length <= 0) {
        if (this.debugElements[id].anchor) {
          this.debugElements[id].anchor.remove();
        }
        this.debugElements[id].remove();
        delete this.debugElements[id];
      }
    });
  }

  this.cy.elements().forEach(e => {
    if (e.isNode()) {
      switch (true) {
        case e.data('isNode'):
          this.renderNode(e);
          break;
        case e.data('isOutput'):
          this.renderOutput(e);
          break;
        case e.id() === 'cursor-node':
          e.style({
            'z-compound-depth': 'bottom',
          });
        case e.hasClass('eh-ghost'):
          if (this.debug) {
            this.renderDebugNode(e, 'green');
          }
          break;
        default:
          throw new Error(`Undefined node type: ${e.id()}`);
      }
    } else {
      this.renderEdge(e);
    }
  });

  if (this.rerender) {
    this.rerender = false;
    this.render();
  }
};

SvgRenderer.prototype.parentNode = function (options) {};

SvgRenderer.prototype.renderEdge = function (edge) {
  const id = edge.id();
  const cy = this.cy;
  const zoom = cy.zoom();
  const pan = cy.pan();
  const rs = edge._private.rscratch;

  if (!rs.allpts || !zoom || !pan) {
    return;
  }

  const size = edge.pstyle('width').pfValue + 3;
  const arrowPadding = 4;

  const arrowPts = [
    rs.arrowEndX - size - arrowPadding,
    rs.arrowEndY - size,
    rs.arrowEndX - size - arrowPadding,
    rs.arrowEndY + size,
    rs.arrowEndX,
    rs.arrowEndY,
  ].map((p, i) => p * zoom + (i % 2 === 0 ? pan.x : pan.y));
  const ghost = edge.id() === 'ghost-edge';

  const startX = rs.startX * zoom + pan.x;
  const startY = rs.startY * zoom + pan.y;
  const endX = rs.endX * zoom + pan.x;
  const endY = rs.endY * zoom + pan.y;

  const gap = Math.min(10, Math.abs((endY - startY) / 2), Math.abs((endX - startX) / 2));

  const horizontalModifier = endX - startX > 0 ? 1 : -1;
  const verticalModifier = endY - startY > 0 ? 1 : -1;
  const horizontalLineWidth = (endX - startX - gap * 2 * horizontalModifier) / 2;
  const verticalLineHeight = endY - startY - gap * 2 * verticalModifier;

  const path = `M ${startX} ${startY} h ${horizontalLineWidth}
  q ${horizontalModifier * gap} 0 ${horizontalModifier * gap} ${verticalModifier * gap}
  v ${verticalLineHeight}
  q 0 ${verticalModifier * gap} ${horizontalModifier * gap} ${verticalModifier * gap}
  h ${horizontalLineWidth}
`;

  if (ghost) {
    ghostEdgeColor = 'blue';
  } else {
    ghostEdgeColor = getRandomColor();
  }

  if (!this.elements[id]) {
    this.elements[id] = this.svg.path().attr({
      class: 'edge',
      id: edge.id(),
      style: `stroke: ${ghostEdgeColor}`,
    });

    this.elements[id].color = ghostEdgeColor;
    this.elements[id].target = edge.data('target');
    this.elements[id].source = edge.data('source');
    this.elements[id].controller = edge.data('controller');
    this.elements[id].showArrow = edge.data('showArrow');

    this.elements[id].arrow = this.svg.polygon().attr({
      style: `fill: ${ghostEdgeColor}`,
    });

    this.initEdgeController(this.elements[id]);
  }

  if (!this.elements[id].showArrow && !ghost) {
    return;
  }

  this.elements[id].plot(path);
  this.elements[id].attr({ zoom });

  if (!ghost) {
    const source = cy.getElementById(edge.data('source'));
    const sourcePosition = source.position();
    const { x, y } = edge.midpoint();
    const controllerElement = this.elements[id].controller.host.nativeElement;
    controllerElement.style.top = `${y - sourcePosition.y - zoom }px`;
    controllerElement.style.left = `${
      x - sourcePosition.x - controllerElement.clientWidth / 2 - zoom - source.width() * 2
    }px`;
    controllerElement.style.transform = `scale(${zoom})`;
    this.elements[id].controller.edgePath = this.elements[id].node;
  }

  this.elements[id].node.style.strokeWidth = `${3 * zoom}px`;

  if (arrowPts.some(pts => !pts)) {
    return;
  }

  this.elements[id].arrow.attr('points', arrowPts).attr({
    class: 'edge-arrow-head',
    transform: ghost ? `rotate(${Math.atan2(rs.endY - rs.startY, rs.endX - rs.startX) * 57} 0 0)` : undefined,
  });
};

SvgRenderer.prototype.renderNode = function (node) {
  if (this.debug) {
    this.renderDebugNode(node, 'red');
  }
  const id = node.id();
  const zoom = this.cy.zoom();

  let element: HTMLElement = this.elements[id];
  const directive: NgFlowDesignerNodeDirective = node.data('directive');
  if (!element) {
    element = directive.host.nativeElement;
    element.setAttribute('id', node.id());
    element.style.position = 'absolute';
    element.style.zIndex = '10';
    // this.html.append(element);
  }
  const position = node.renderedPosition();

  const rect = element.getBoundingClientRect();
  const w = rect.width / zoom;
  const h = rect.height / zoom;
  if (node.height() !== w || node.width() !== h) {
    node.style({
      width: w,
      height: h,
      'z-compound-depth': 'orphan',
      // "z-compound-depth": "bottom",
    });
  }
  element.style.transform = `translate(${position.x - node.width() / 2}px, ${
    position.y - node.height() / 2
  }px) scale(${zoom})`;
  element.style.opacity = this.debug ? '0.2' : '1';

  this.elements[id] = element;
};

SvgRenderer.prototype.renderDebugNode = function (node, color) {
  const position = node.renderedPosition();
  const box = node.renderedBoundingBox();
  const id = node.id();

  if (!this.debugElements[id]) {
    this.debugElements[id] = this.svg.rect(box.w, box.h);

    this.debugElements[id].anchor = this.svg.rect(5, 5);
  }

  const element = this.debugElements[id];
  element.move(box.x1, box.y1);
  element.anchor.move(position.x, position.y);
  element.attr({ width: box.w, height: box.h, fill: color });
};

SvgRenderer.prototype.renderOutput = function (output) {
  if (this.debug) {
    this.renderDebugNode(output, 'blue');
  }
  const outputDirective: NgFlowDesignerOutputDirective = output.data('directive');
  const containerBox = this.cy.container().getBoundingClientRect();
  const outputBox = outputDirective.host.nativeElement.getBoundingClientRect();
  const outputPosition = output.position();
  const zoom = this.cy.zoom();

  const w = outputBox.width / zoom;
  const h = outputBox.height / zoom;
  if (output.height() !== h || output.width() !== w) {
    output.style({
      width: w,
      height: h,
      'z-compound-depth': 'top',
    });
  }
  const x = outputBox.x - containerBox.x + outputBox.width / 2;
  const y = outputBox.y - containerBox.y + outputBox.height / 2;

  if (outputPosition.x !== x || outputPosition.y !== y) {
    output.renderedPosition({ x, y });
  }
};
